import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@/contexts/DataCacheProvider";
import {
  getCurrentUser,
  updateUserData,
  assignDirector,
  removeOrganization,
  RemoveOrganizationData,
} from "@/services/contacts";
import { useAccount } from "@azure/msal-react";

import { MsalAccountInfo } from "@/types/auth.types";

export const CURRENT_USER_CACHE_KEY = "currentUserProfile";

export const useCurrentUser = () => {
  const currentUser: MsalAccountInfo | null = useAccount();
  const enabled = !!currentUser?.idToken;
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: [CURRENT_USER_CACHE_KEY],
    queryFn: getCurrentUser,
    staleTime: Infinity,
    enabled,
  });

  return {
    ...queryResult,
    invalidate: () => {
      return queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_CACHE_KEY],
      });
    },
  };
};

export function useUpdateUserProfile() {
  const mutation = useMutation({
    mutationFn: updateUserData,
  });

  return {
    mutate: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error as Error | null,
    data: mutation.data,
  };
}

export function useRemoveOrganization() {
  const mutation = useMutation({
    mutationFn: ({ data }: { data: RemoveOrganizationData }) =>
      removeOrganization(data),
  });

  return {
    mutate: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error as Error | null,
    data: mutation.data,
  };
}

export function useAssignDirector() {
  const mutation = useMutation({
    mutationFn: ({
      users,
      accountId,
    }: {
      users: { ContactId: string; IsDirector: boolean }[];
      accountId: string;
    }) => assignDirector(users, accountId),
  });

  return {
    mutate: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error as Error | null,
    data: mutation.data,
  };
}
